import { TFunction } from 'i18next';
import {
  HealthSystemDomainAnimalsQueriesAnimalObservationCategoryOption,
  HealthSystemDomainAnimalsQueriesGeneticFailureTypeOption
} from '../api/HealthSystemApi';

/**
 * Helpers for the non-fatal observations registered against a live animal - genetic failures,
 * biters and bite victims (AB#3724).
 *
 * Both the category and the genetic failure arrive as the backend's string enumeration ids
 * (AnimalObservationCategoryEnumeration / GeneticFailureTypeEnumeration), not as numeric enums,
 * so they are plain strings on the wire.
 */

export const AnimalObservationCategory = {
  GeneticFailure: 'GeneticFailure',
  Biter: 'Biter',
  BiteVictim: 'BiteVictim'
} as const;

/** The id the backend uses for a defect the farmer typed in by hand. */
export const GENETIC_FAILURE_OTHER = 'Other';

// Categories are a closed set on the backend, so they get explicit keys.
const CATEGORY_LABEL_KEYS: Record<string, string> = {
  [AnimalObservationCategory.GeneticFailure]: 'f-animal-observation-genetic-failure',
  [AnimalObservationCategory.Biter]: 'f-animal-observation-biter',
  [AnimalObservationCategory.BiteVictim]: 'f-animal-observation-bite-victim'
};

export const observationCategoryLabel = (t: TFunction, category: string): string => {
  const key = CATEGORY_LABEL_KEYS[category];
  return key ? t(key) : category;
};

/**
 * Genetic failures are deliberately open-ended: the backend adds a defect with a single line and
 * no migration, so a client that has not shipped a translation yet must still render something
 * readable. An id with no key falls back to the id itself rather than to a missing-key string.
 */
export const geneticFailureLabel = (t: TFunction, geneticFailure: string): string => {
  const key = `f-animal-genetic-failure-${geneticFailure.toLowerCase()}`;
  const label = t(key);
  return label === key ? geneticFailure : label;
};

export interface ObservationLabelSource {
  category: string;
  geneticFailure?: string | null;
  geneticFailureOther?: string | null;
}

/**
 * One observation as a single line - "Genetic failure: Belly-button issue", or just "Biter" for
 * the categories that have no sub-type. A defect of "Other" shows what the farmer typed.
 */
export const observationLabel = (t: TFunction, observation: ObservationLabelSource): string => {
  const category = observationCategoryLabel(t, observation.category);

  if (!observation.geneticFailure) {
    return category;
  }

  const defect =
    observation.geneticFailure === GENETIC_FAILURE_OTHER && observation.geneticFailureOther
      ? observation.geneticFailureOther
      : geneticFailureLabel(t, observation.geneticFailure);

  return `${category}: ${defect}`;
};

/**
 * The observation dropdown's options are the API's own response shape rather than a copy of it: the
 * response is the contract, and a hand-written duplicate only creates somewhere for the two to
 * drift apart.
 */
export type ObservationCategoryOption = HealthSystemDomainAnimalsQueriesAnimalObservationCategoryOption;
export type GeneticFailureOption = HealthSystemDomainAnimalsQueriesGeneticFailureTypeOption;

/**
 * Reads the defects nested under a category, tolerating the shape the endpoint used to send.
 *
 * `geneticFailures` used to be bare id strings and is now `{ geneticFailure, description }` objects
 * carrying the backend's own translated label - which is where the AgroVision defect codes get
 * their wording, since this client has a key for `Other` alone. The generated client describes the
 * current shape correctly, so the types are honest; what they cannot promise is *which* backend
 * answers, because web and backend deploy independently and an older one still sends ids.
 *
 * That skew is worth a few lines here: interpolating the object shape into a label threw during
 * render, so it took the whole animal search page to `/error` rather than degrading one dropdown.
 * Reading both shapes at the boundary means everything downstream gets ids that really are strings.
 */
export const geneticFailureOptionsFrom = (raw: readonly unknown[] | null | undefined): GeneticFailureOption[] => {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw.flatMap((entry) => {
    // The old wire shape: an id, with no label of its own.
    if (typeof entry === 'string') {
      return [{ geneticFailure: entry, description: null }];
    }

    const option = entry as GeneticFailureOption | null;

    return option && typeof option.geneticFailure === 'string' ? [option] : [];
  });
};
